'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

const HealthGaugeChart = dynamic(() => import('./HealthGaugeChart'), { ssr: false });

// Backend integration: GET /api/seller/account-health
const healthScore = 78;

const metrics = [
  { id: 'metric-odr', label: 'Order Defect Rate', value: '0.8%', target: '<1%', status: 'good' as const },
  { id: 'metric-lsr', label: 'Late Shipment', value: '3.2%', target: '<4%', status: 'good' as const },
  { id: 'metric-cr', label: 'Cancellation Rate', value: '2.8%', target: '<2.5%', status: 'critical' as const },
  { id: 'metric-rrr', label: 'Return Rate', value: '4.1%', target: '<5%', status: 'good' as const },
  { id: 'metric-rt', label: 'Response Time', value: '18h', target: '<24h', status: 'good' as const },
  { id: 'metric-pqr', label: 'Product Quality', value: '4.2★', target: '≥4.0★', status: 'good' as const },
  { id: 'metric-otd', label: 'On-Time Delivery', value: '91.4%', target: '>95%', status: 'warning' as const },
  { id: 'metric-vtr', label: 'Valid Tracking', value: '97.8%', target: '>95%', status: 'good' as const },
  { id: 'metric-csat', label: 'Customer Sat.', value: '82%', target: '>85%', status: 'warning' as const },
];

const getHealthLevel = (score: number) => {
  if (score >= 90) return { label: 'Excellent', color: 'text-success', bg: 'bg-success/10', border: 'border-success/30' };
  if (score >= 80) return { label: 'Good', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' };
  if (score >= 70) return { label: 'At Risk', color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30' };
  return { label: 'Poor', color: 'text-danger', bg: 'bg-danger/10', border: 'border-danger/30' };
};

const statusConfig = {
  good: { label: 'Good', className: 'badge-success' },
  warning: { label: 'Warning', className: 'badge-warning' },
  critical: { label: 'Critical', className: 'badge-danger' },
};

export default function AccountHealthCard() {
  const level = getHealthLevel(healthScore);
  const criticalMetrics = metrics.filter((m) => m.status === 'critical');
  const warningMetrics = metrics.filter((m) => m.status === 'warning');

  return (
    <div className="card p-5 h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className={level.color} />
          <h2 className="text-base font-semibold text-foreground">Account Health</h2>
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded-full border ${level.color} ${level.bg} ${level.border}`}>
          {level.label}
        </span>
      </div>

      {/* Score gauge */}
      <div className="flex flex-col items-center mb-5">
        <HealthGaugeChart score={healthScore} />
        <p className="text-xs text-muted-foreground mt-1">6-month avg: 81%</p>
      </div>

      {/* Alerts */}
      {(criticalMetrics.length > 0 || warningMetrics.length > 0) && (
        <div className="mb-4 p-3 rounded-lg bg-warning/10 border border-warning/20 space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-warning mb-2">
            <AlertTriangle size={13} />
            Action Required
          </div>
          {criticalMetrics.map((m) => (
            <p key={`alert-${m.id}`} className="text-xs text-danger">
              ⚠ {m.label} is {m.value} — target {m.target}
            </p>
          ))}
          {warningMetrics.map((m) => (
            <p key={`warn-${m.id}`} className="text-xs text-warning">
              ↗ {m.label} at {m.value} — target {m.target}
            </p>
          ))}
        </div>
      )}

      {/* Metrics grid 3x3 */}
      <div className="grid grid-cols-3 gap-2">
        {metrics.map((metric) => (
          <div
            key={metric.id}
            className="p-2 rounded-lg bg-muted/50 border border-border hover:border-primary/20 transition-colors"
          >
            <p className="text-2xs text-muted-foreground leading-tight mb-1 truncate" title={metric.label}>
              {metric.label}
            </p>
            <p className="text-sm font-bold text-foreground tabular-nums">{metric.value}</p>
            <span className={statusConfig[metric.status].className + ' mt-1'}>
              {statusConfig[metric.status].label}
            </span>
          </div>
        ))}
      </div>

      {/* View full report */}
      <button className="w-full mt-4 text-xs font-semibold text-primary hover:underline text-center">
        View Full Health Report →
      </button>
    </div>
  );
}